import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Modal, RadioGroup, Radio, Checkbox, Switch, Box, Button, IconButton, TextField, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import '../translations.js';
import './rescrapingSettingsModal.css';
import InfoTooltip from './InfoTooltip.js';

// --- base-URL membership helpers (mirror the backend scoping) ---
const stripWww = (h) => (h && h.startsWith('www.') ? h.slice(4) : h);

const hostAndPath = (raw) => {
  if (!raw) return ['', ''];
  let u = String(raw).trim();
  if (!/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//.test(u)) u = 'https://' + u;
  try {
    const parsed = new URL(u);
    let path = parsed.pathname || '';
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
    return [(parsed.host || '').toLowerCase(), path === '/' ? '' : path];
  } catch {
    return ['', ''];
  }
};

const urlUnderBase = (url, baseHost, basePath) => {
  const [h, p] = hostAndPath(url);
  if (!h || stripWww(h) !== stripWww(baseHost)) return false;
  const bp = (basePath || '').replace(/\/+$/, '');
  if (!bp) return true;
  return p === bp || p.startsWith(bp + '/');
};

const RescrapingSettingsModal = ({ open, onClose, onSave, bot, onOpenSubscriptionModal }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const websiteLinks = useMemo(
    () => bot?.knowledge_data?.filter((i) => i.type === 'url_link') || [],
    [bot]
  );

  const [frequency, setFrequency] = useState(bot?.rescraping_task?.interval ?? 'monthly');
  const [fetchNewLinks, setFetchNewLinks] = useState(bot?.rescraping_task?.fetch_new_links ?? true);
  const [removeOldLinks, setRemoveOldLinks] = useState(bot?.rescraping_task?.remove_old_links ?? false);
  const [knowledgeDataIds, setKnowledgeDataIds] = useState(
    bot?.rescraping_task?.knowledge_data_ids !== undefined
      ? bot.rescraping_task.knowledge_data_ids
      : websiteLinks.map((l) => l.id)
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [baseUrls, setBaseUrls] = useState(bot?.rescraping_task?.base_urls ?? []);

  useEffect(() => {
    if (!open) return;

    setFrequency(bot?.rescraping_task?.interval ?? 'monthly');
    setKnowledgeDataIds(
      bot?.rescraping_task?.knowledge_data_ids !== undefined
        ? bot.rescraping_task.knowledge_data_ids
        : websiteLinks.map((l) => l.id)
    );
    setFetchNewLinks(bot?.rescraping_task?.fetch_new_links ?? true);
    setRemoveOldLinks(bot?.rescraping_task?.remove_old_links ?? false);
    setSearchTerm('');
    setBaseUrls(bot?.rescraping_task?.base_urls ?? []);
  }, [open, bot, websiteLinks]);

  // "Other" links = those NOT under any entered base URL. Only these are shown
  // for opt-in refresh; base-covered links are synced automatically.
  const parsedBases = useMemo(
    () => baseUrls.map((b) => hostAndPath(b)).filter(([h]) => h),
    [baseUrls]
  );
  const orphanLinks = useMemo(
    () =>
      websiteLinks.filter(
        (l) => !parsedBases.some(([h, p]) => urlUnderBase(l.url_link || l.url, h, p))
      ),
    [websiteLinks, parsedBases]
  );

  const allSelected =
    orphanLinks.length > 0 && orphanLinks.every((l) => knowledgeDataIds.includes(l.id));

  const filteredLinks = orphanLinks.filter((l) =>
    (l.url_link || l.url).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) setKnowledgeDataIds(orphanLinks.map((l) => l.id));
    else setKnowledgeDataIds([]);
  };

  const handleToggleLink = (id) => (e) => {
    if (e.target.checked) setKnowledgeDataIds((prev) => [...prev, id]);
    else setKnowledgeDataIds((prev) => prev.filter((x) => x !== id));
  };

  const handleAddBaseUrl = () => setBaseUrls((prev) => [...prev, '']);
  const handleRemoveBaseUrl = (idx) =>
    setBaseUrls((prev) => prev.filter((_, i) => i !== idx));
  const handleChangeBaseUrl = (idx, val) =>
    setBaseUrls((prev) => prev.map((u, i) => (i === idx ? val : u)));

  const handleSave = () => {
    // Drop blanks; backend also trims/dedupes/caps.
    const cleanedBaseUrls = baseUrls.map((u) => (u || '').trim()).filter(Boolean);
    // knowledge_data_ids now means "extra links NOT under a base URL to also
    // refresh" — persist only the ticked other-links.
    const orphanIdSet = new Set(orphanLinks.map((l) => l.id));
    const otherLinkIds = knowledgeDataIds.filter((id) => orphanIdSet.has(id));
    onSave({ frequency, knowledgeDataIds: otherLinkIds, fetchNewLinks, removeOldLinks, baseUrls: cleanedBaseUrls });
    onClose();
  };

  if (!open) return null;

  const plan = bot?.subscription_plan?.name;
  const hasAccess = plan === "Boost" || plan === "Ultimo";

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        outline: 'none',
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "20px",
          width: "100%",
          maxWidth: isMobile ? "80%" : "600px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          outline: 'none',
          position: 'relative',
        }}
      >
        {!hasAccess ? (
          <>
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
              aria-label="close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
            <div className="rescraping-upgrade-modal-content">
              <div className="rescraping-upgrade-modal-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              <h2 className="rescraping-upgrade-modal-title">{t('live_inbox_suggest_upgrade_title')}</h2>

              <p className="rescraping-upgrade-modal-description">
                {t('rescraping_upgrade_description')}
              </p>

              <div className="rescraping-upgrade-plan-info">
                <div className="rescraping-upgrade-plan-badge rescraping-upgrade-current-plan">
                  <span className="rescraping-upgrade-plan-label">{t('current_plan')}</span>
                  <span className="rescraping-upgrade-plan-name">{bot?.subscription_plan?.name || t('plan_free')}</span>
                </div>
                <div className="rescraping-upgrade-arrow-icon">→</div>
                <div className="rescraping-upgrade-plan-badge rescraping-upgrade-required-plan">
                  <span className="rescraping-upgrade-plan-label">{t('skill_required')}</span>
                  <span className="rescraping-upgrade-plan-name">Boost+</span>
                </div>
              </div>

              <button
                className="rescraping-upgrade-button"
                onClick={() => onOpenSubscriptionModal(bot)}
              >
                {t('upgrade_now')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="rs-title">{t('rescraping_settings')}</h2>

            {/* frequency */}
            <div className="rs-section">
              <span className="rs-label">
                {t('rescraping_frequency')}:
                <InfoTooltip text={t('rescraping_frequency_tooltip')} />
              </span>

              <RadioGroup
                row
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="rs-radio-group"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1.5rem',
                  marginTop: '0.5rem',
                }}
              >
                {/* Monthly option */}
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Radio
                    value="monthly"
                    sx={{
                      mr: -0.5,
                      color: plan === 'Ultimo' ? '#5616ea' : '#9e9e9e',
                      '& .MuiSvgIcon-root': {
                        transition: 'color 0.2s ease',
                        color: plan === 'Ultimo' ? '#5616ea' : '#b0b0b0',
                      },
                      '&.Mui-checked .MuiSvgIcon-root': {
                        color: '#5616ea',
                      },
                    }}
                  />
                  <span
                    style={{ fontSize: '0.95rem', color: '#333', cursor: 'pointer' }}
                    onClick={() => setFrequency('monthly')}
                  >
                    {t('monthly')}
                  </span>
                </Box>

                {/* Daily option */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.8}
                    sx={{
                      cursor: plan === 'Ultimo' ? 'pointer' : 'not-allowed',
                      opacity: plan === 'Ultimo' ? 1 : 0.8,
                      '&:hover': {
                        opacity: plan === 'Ultimo' ? 1 : 0.9,
                      },
                    }}
                  >
                    <Radio
                      value="daily"
                      disabled={plan !== 'Ultimo'}
                      sx={{
                        mr: -0.5,
                        color: plan === 'Ultimo' ? '#5616ea' : '#bdbdbd',
                        '& .MuiSvgIcon-root': {
                          transition: 'color 0.2s ease',
                          color: plan === 'Ultimo' ? '#5616ea' : '#d0d0d0',
                        },
                        '&.Mui-checked .MuiSvgIcon-root': {
                          color: '#5616ea',
                        },
                        '&.Mui-disabled .MuiSvgIcon-root': {
                          color: '#cfcfcf',
                        },
                      }}
                    />
                    <span
                      style={{
                        fontSize: '0.95rem',
                        color: plan === 'Ultimo' ? '#333' : '#777',
                        cursor: plan === 'Ultimo' ? 'pointer' : 'not-allowed',
                      }}
                      onClick={() => plan === 'Ultimo' && setFrequency('daily')}
                    >
                      {t('daily')}
                    </span>
                  </Box>

                  {/* Upgrade info (only if not Ultimo) */}
                  {plan !== 'Ultimo' && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        backgroundColor: '#f9f9ff',
                        border: '1px solid #e0e0f8',
                        borderRadius: '8px',
                        padding: '4px 10px',
                        marginLeft: '0.4rem',
                        cursor: 'default',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.85rem',
                          color: '#5616ea',
                          fontWeight: 500,
                        }}
                      >
                        {t('ultimo_plan_required')}
                      </span>
                      <Button
                        size="small"
                        onClick={() => onOpenSubscriptionModal(bot)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          padding: '2px 8px',
                          minWidth: 'auto',
                          color: '#ffffff',
                          backgroundColor: '#5616ea',
                          borderRadius: '6px',
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#4a14d6' },
                        }}
                      >
                        {t('upgrade')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </RadioGroup>
            </div>

            {/* base URLs — the sites we crawl to discover / prune links */}
            <div className="rs-section">
              <span className="rs-label" style={{ display: 'block', marginBottom: '14px' }}>
                {t('base_urls_label')}:
                <InfoTooltip text={t('base_urls_tooltip')} />
              </span>
              <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {baseUrls.length === 0 && (
                  <div style={{ fontSize: '0.85rem', color: '#777' }}>
                    {t('base_urls_empty')}
                  </div>
                )}
                {baseUrls.map((url, idx) => (
                  <TextField
                    key={idx}
                    label={`${t('base_url_field_label')}${baseUrls.length > 1 ? ` ${idx + 1}` : ''}`}
                    placeholder={t('base_url_placeholder')}
                    value={url}
                    onChange={(e) => handleChangeBaseUrl(idx, e.target.value)}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t('remove')}
                            onClick={() => handleRemoveBaseUrl(idx)}
                            edge="end"
                            size="small"
                            sx={{ color: 'text.secondary' }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                        border: '1px solid #515151',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'gray',
                        '&.Mui-focused': { color: '#5616ea' },
                      },
                    }}
                  />
                ))}
              </div>
              <button
                onClick={handleAddBaseUrl}
                className="add-email-button"
                title={t('add_base_url')}
                type="button"
                style={{ marginTop: '4px' }}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            {/* switch */}
            <div className="rs-section rs-switch-row">
              <span className="rs-label-new-links">
                {t('scan_new_links')}:
                <InfoTooltip text={t('scan_new_links_tooltip')} />
              </span>
              <div className="new-links-text-toggle">
                <Switch
                  checked={fetchNewLinks}
                  onChange={(e) => setFetchNewLinks(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
                    '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                  }}
                />
                <span className="rs-switch-text">{t('fetch_new_links')}</span>
              </div>
            </div>

            {/* remove old links switch */}
            <div className="rs-section rs-switch-row">
              <span className="rs-label-new-links">
                {t('remove_old_links')}:
                <InfoTooltip text={t('remove_old_links_tooltip')} />
              </span>
              <div className="new-links-text-toggle">
                <Switch
                  checked={removeOldLinks}
                  onChange={(e) => setRemoveOldLinks(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
                    '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                  }}
                />
                <span className="rs-switch-text">{t('remove_old_links_label')}</span>
              </div>
            </div>

            {/* "other" links (not under any base URL) — only shown when some exist */}
            {orphanLinks.length > 0 && (
              <>
                <span className="rs-label-links-selection">
                  {t('other_links_label')}:
                  <InfoTooltip text={t('other_links_tooltip')} />
                </span>
                <div className="rs-section">
                  <div className="rs-search-wrapper">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="rs-search-icon" />
                    <input
                      className="rs-search-input"
                      type="text"
                      placeholder={t('search_links')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button className="rs-clear-button" onClick={() => setSearchTerm('')} type="button">
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {filteredLinks.length === 0 ? (
                  <div className="rs-no-links">{t('no_matching_links')}</div>
                ) : (
                  <div className="rs-link-box">
                    {searchTerm.trim() === '' && (
                      <label className="rs-checkbox-header">
                        <Checkbox
                          checked={allSelected}
                          onChange={handleSelectAll}
                          sx={{
                            color: '#5616ea',
                            '&.Mui-checked': { color: '#5616ea' },
                          }}
                          className="row-checkbox"
                        />
                        {t('select_all')}
                      </label>
                    )}

                    {filteredLinks.map((link) => (
                      <label key={link.id} className="rs-checkbox-row">
                        <Checkbox
                          checked={knowledgeDataIds.includes(link.id)}
                          onChange={handleToggleLink(link.id)}
                          sx={{
                            color: '#5616ea',
                            '&.Mui-checked': { color: '#5616ea' },
                          }}
                          className="row-checkbox"
                        />
                        {link.url_link || link.url}
                      </label>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} marginTop={2}>
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{
                  color: "#5616ea",
                  borderColor: "#5616ea",
                  "&:hover": { borderColor: "#5e0fb3", backgroundColor: "#f3eaff" },
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{
                  backgroundColor: "#5616ea",
                  "&:hover": { backgroundColor: "#4a14d6" },
                }}
              >
                {t("save")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default RescrapingSettingsModal;
